import { notifyWarning } from '@/bugsnag';
import Observability from '@launchdarkly/observability';
// import SessionReplay from '@launchdarkly/session-replay';
import type {
    LDContext,
    LDOptions,
    LDVueClient,
    LDVueInstance,
    LDVuePluginOptions,
    LDWaitForInitializationResult,
} from '@launchdarkly/vue-client-sdk';
import { LDVueInstanceKey, LDVuePlugin } from '@launchdarkly/vue-client-sdk';
import type { Plugin } from 'vue';
import { inject } from 'vue';

const INITIALIZATION_TIMEOUT_SECONDS = 5;
const APPLICATION_ID = 'closinglock-client-portal';
const BUGSNAG_CONTEXT = 'LaunchDarkly initialization';

/**
 * LDVuePlugin creates a new client on every install, and a client portal page mounts several Vue apps.
 * The first install creates the client, and later apps are given the same instance.
 */
export let sharedLDInstance: LDVueInstance | undefined;

const buildContext = (): LDContext => {
    const clientId = window.options?.client_id;
    console.log({ clientId });

    return clientId
        ? { kind: 'user', key: String(clientId) }
        : {
              kind: 'user',
              key: 'certifid-anon-user',
              anonymous: false,
          };
};

const buildLdOptions = (): LDOptions => {
    const applicationInfo = {
        id: APPLICATION_ID,
        name: 'Closinglock Client Portal',
        version: window.options?.appVersion,
    };
    const devServerUrl = import.meta.env.VITE_LAUNCHDARKLY_DEV_SERVER_URL;

    if (devServerUrl) {
        return {
            applicationInfo,
            baseUri: devServerUrl,
            streamUri: devServerUrl,
            eventsUri: devServerUrl,
        };
    }

    return {
        applicationInfo,
        plugins: [
            new Observability({
                serviceName: 'client-portal',
                version: window.options?.appVersion,
                environment: import.meta.env.VITE_APP_ENV,
                tracingOrigins: true,
                networkRecording: { enabled: true, recordHeadersAndBody: false },
                consoleMethodsToRecord: ['warn', 'error'],
            }),
            // new SessionReplay({
            //     enableCanvasRecording: true, // enable canvas recording
            //     samplingStrategy: {
            //         canvas: 2, // snapshot at 2 fps
            //         canvasMaxSnapshotDimension: 480, // snapshot at a max 480p resolution
            //     },
            // }),
        ],
    };
};

const reportInitializationResult = (result: LDWaitForInitializationResult) => {
    if (result.status === 'timeout') {
        notifyWarning('LaunchDarkly initialization timed out', BUGSNAG_CONTEXT);
    } else if (result.status === 'failed') {
        notifyWarning(result.error, BUGSNAG_CONTEXT);
    } else if (result.status === 'complete') {
        console.info('LD initialized');
    }
};

/** Skips pages entering the back/forward cache, which would otherwise be restored with a closed client */
const closeOnPageHide = (client: LDVueClient) => {
    window.addEventListener('pagehide', event => {
        if (!event.persisted) {
            client.close();
        }
    });
};

/**
 * Provides one shared LaunchDarkly client to every Vue app it is installed on.
 *
 * Components read flags with `useBoolVariation`, `useStringVariation`, `useNumberVariation` or
 * `useJsonVariation`, and readiness with `useInitializationStatus`, from `@launchdarkly/vue-client-sdk`.
 * Without a client-side ID the client is never started, so those composables return their defaults.
 */
export const getLaunchDarklyVuePlugin = (): Plugin => app => {
    if (sharedLDInstance) {
        app.provide(LDVueInstanceKey, sharedLDInstance);
        return;
    }

    const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID ?? '';
    console.assert(clientSideID !== '', 'VITE_LAUNCHDARKLY_CLIENT_SIDE_ID env var not found');

    const pluginOptions: LDVuePluginOptions = {
        clientSideID,
        context: buildContext(),
        deferInitialization: true,
        ldOptions: clientSideID ? buildLdOptions() : { sendEvents: false },
    };
    app.use(LDVuePlugin, pluginOptions);

    sharedLDInstance = app.runWithContext(() => inject(LDVueInstanceKey));
    if (!sharedLDInstance || !clientSideID) {
        return;
    }

    const { client } = sharedLDInstance;
    client.start({ timeout: INITIALIZATION_TIMEOUT_SECONDS }).then(reportInitializationResult);
    // console.log('initial flag value:', client.boolVariation('enable-plaid-bav', false));

    closeOnPageHide(client);
};
